import Alert, { type AlertColor, type AlertProps } from '@mui/material/Alert';
import Snackbar, {
  type SnackbarCloseReason,
  type SnackbarProps,
} from '@mui/material/Snackbar';
import type { ReactNode, SyntheticEvent } from 'react';
import { useLaptopLayoutAndUp } from '../../utils/useLaptopLayoutAndUp';
import { SlideTransition } from '../SlideTransition';

const AUTO_HIDE_DURATION_MS = 5000;

export type AlertSnackbarProps = Omit<
  SnackbarProps,
  'message' | 'children' | 'onClose'
> & {
  severity: AlertColor;
  message?: ReactNode;
  // `reason` is optional since the alert's close button and click don't provide one.
  onClose?: (
    event: SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => void;
  alertProps?: Omit<AlertProps, 'severity' | 'children'>;
};

export const AlertSnackbar = ({
  severity,
  message,
  onClose,
  alertProps,
  ...props
}: AlertSnackbarProps) => {
  const laptopLayout = useLaptopLayoutAndUp();

  return (
    <Snackbar
      autoHideDuration={AUTO_HIDE_DURATION_MS}
      // Top right on larger screens to stay clear of the sticky action buttons.
      anchorOrigin={{
        vertical: laptopLayout ? 'top' : 'bottom',
        horizontal: laptopLayout ? 'right' : 'center',
      }}
      slots={laptopLayout ? undefined : { transition: SlideTransition }}
      onClose={onClose}
      {...props}
    >
      <Alert
        severity={severity}
        onClose={onClose}
        // The whole toast closes on click; the close button stays for keyboard users.
        onClick={onClose}
        sx={{ width: '100%', cursor: onClose ? 'pointer' : undefined }}
        {...alertProps}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};
